import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { formatPrice } from "../../../../../utilities/formatPrice";
import localeText from "../../../../../locale";
import OrderService from "../../../../../service/OrderService";
import { cloudinaryInstance } from "../../../../../utilities/Cloudinary/cloudinaryInstance";
import { formatDateTime } from "../../../../../utilities/formatDate";

const orderService = new OrderService();
const RECENT_LIMIT = 4;
const RECENT_FETCH_SIZE = 20;

function getTimeAgoLabel(dateStamp) {
  if (!dateStamp) return "";
  const t = new Date(dateStamp).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return localeText("recentOperationsJustNow") || "agora";
  if (mins < 60) return `${mins} min`;
  if (hours < 24) return `${hours} h`;
  return `${days}d`;
}

function mapOrderToOperation(order, loggedUsername) {
  const first = order?.itens?.[0];
  const product = first?.product;
  const variation = first?.productVariation;
  const imageId = variation?.imageid || product?.imageid1;
  const extraCount = (order?.itens?.length ?? 0) - 1;
  let productName = product?.title || product?.name || "";
  if (!productName) {
    const buyerUsername = order?.buyer?.username;
    const isPurchase = buyerUsername && loggedUsername && buyerUsername === loggedUsername;
    productName =
      (isPurchase ? localeText("purchaseInStore") : localeText("saleInStore")) ||
      localeText("transaction") ||
      "—";
  } else if (extraCount > 0) {
    const suffix = localeText("recentOperationsMoreItems", { count: extraCount });
    productName = `${productName} ${suffix || `(+${extraCount})`}`;
  }

  const value = order.amountTotal ?? order.amountOrder ?? 0;
  const timeLabel =
    getTimeAgoLabel(order.date) ||
    (order.date && !Number.isNaN(new Date(order.date).getTime()) ? formatDateTime(order.date) : "");

  return {
    id: order.id,
    productName,
    price: value,
    ticket: String(order.id),
    timeAgo: timeLabel,
    imageId,
  };
}

const RecentOperations = ({ isCustomerDashboard = false, currencySymbol = "€" }) => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const token = localStorage.getItem("__extraconomy_token");
        if (!token) {
          if (!cancelled) setOperations([]);
          return;
        }
        // PENDING = carrinho criado e não concluído: não é uma operação. Busca uma página maior
        // e filtra aqui até a API aplicar o excludeStatus (versões antigas ignoram o parâmetro).
        const res = await orderService.getAllTransactions(token, {
          page: 1,
          pageSize: RECENT_FETCH_SIZE,
          excludeStatus: "PENDING",
        });
        if (cancelled) return;
        if (res.messageCode === 200000 || res.messageCode === 200001) {
          const content = Array.isArray(res?.result?.content) ? res.result.content : [];
          const username = localStorage.getItem("__extraconomy_username");
          setOperations(
            content
              .filter((o) => o?.status !== "PENDING")
              .slice(0, RECENT_LIMIT)
              .map((o) => mapOrderToOperation(o, username)),
          );
        } else {
          setOperations([]);
        }
      } catch {
        if (!cancelled) setOperations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {loading ? (
        <div className="flex justify-content-center py-5">
          <i
            className="pi pi-spin pi-spinner text-2xl"
            style={{ color: "var(--theme-text-muted, #7A869A)" }}
          />
        </div>
      ) : operations.length === 0 ? (
        <div
          className="flex flex-column justify-content-center align-items-center gap-2"
          style={{ color: "var(--theme-text-muted, #7A869A)", minHeight: "150px" }}
        >
          {isCustomerDashboard
            ? localeText("recentOperationsCustomerEmptyState") ||
              "Você ainda não realizou operações de compra."
            : localeText("noOperationsRecords") || "Ainda não há registros de operações."}
          <Link
            to="/"
            className="text-sm p-no-outline border-none bg-transparent cursor-pointer hover:underline"
            style={{ color: "var(--theme-primary)" }}
          >
            {localeText("recentOperationsExploreStore") || "Conhecer a loja Extraconomy"}
          </Link>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center justify-content-between mb-3">
            <div className="m-0 font-bold" style={{ color: "var(--theme-text)" }}>
              {isCustomerDashboard ? localeText("myPurchases") : localeText("recentOperations")}
            </div>
            <Link
              to={
                isCustomerDashboard
                  ? "/dashboard-purchases"
                  : "/dashboard-financeiro/carteira?tab=transacoes"
              }
              className="text-sm p-no-outline border-none bg-transparent cursor-pointer hover:underline"
              style={{
                color: isCustomerDashboard ? "var(--theme-primary)" : "var(--theme-secondary)",
              }}
            >
              {localeText("viewAll")}
            </Link>
          </div>
          {operations.map((operation, index) => {
            const isLast = index === operations.length - 1;
            return (
              <div
                key={operation.id}
                className="flex justify-content-between align-items-center pb-3"
                style={
                  isLast ? undefined : { borderBottom: "2px solid var(--theme-border, #EBEBEB)" }
                }
              >
                <div className="flex-1">
                  <div className="flex align-items-center gap-2">
                    <div
                      className="border-round-md flex align-items-center justify-content-center overflow-hidden flex-shrink-0"
                      style={{ width: "40px", height: "40px" }}
                    >
                      {operation.imageId ? (
                        <AdvancedImage
                          cldImg={cloudinaryInstance
                            .image(operation.imageId)
                            .resize(fill().width(80).height(80))
                            .format("webp")}
                          className="object-cover"
                          style={{ objectFit: "cover", width: "40px", height: "40px" }}
                          alt=""
                        />
                      ) : (
                        <div
                          className="w-full h-full flex align-items-center justify-content-center"
                          style={{ backgroundColor: "var(--theme-border, #EBEBEB)" }}
                        >
                          <i
                            className="pi pi-shopping-bag text-sm"
                            style={{ color: "var(--theme-text-muted)" }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-column">
                      <div
                        className={`text-sm font-medium ${isLast ? "mb-0" : "mb-1"}`}
                        style={{ color: "var(--theme-text)" }}
                      >
                        {operation.productName} - {currencySymbol} {formatPrice(operation.price)}
                      </div>
                      <div
                        className="text-xs"
                        style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                      >
                        #{operation.ticket} {operation.timeAgo ? ` · ${operation.timeAgo}` : ""}
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="text-sm font-semibold" style={{ color: "var(--theme-text)" }}>
                  {currencySymbol} {formatPrice(operation.price)}
                </div> */}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default React.memo(RecentOperations);
